/**
 * The location's published hours, collapsed (ORDR-118; FR-STO-024, 025).
 *
 * One line per shift under the day it OPENS, with the closing time exactly as
 * sent: Friday 18:00 to 02:00 is one Friday line, never split across two days.
 * `crosses_midnight` is the server's word for that and nothing is inferred.
 * With a known mode only that mode's shifts are listed; otherwise every mode,
 * labelled by the key the server sent.
 */

import type { ReactElement } from "react";
import { dayName, formatDate } from "./format.js";
import type { ModeSchedule, Schedule, ScheduleException } from "./storefrontApi.js";

export interface PublishedHoursProps {
  readonly schedule: Schedule;
  /** The flow's mode key, or `null` while the MODE step is still REQUIRED. */
  readonly mode: string | null;
}

function shiftLines(entry: ModeSchedule): string[] {
  return [...entry.shifts]
    .sort((a, b) => a.day_of_week - b.day_of_week || a.opens.localeCompare(b.opens))
    .map((shift) => `${dayName(shift.day_of_week)} ${shift.opens} to ${shift.closes}`);
}

function dates(exception: ScheduleException): string {
  const from = formatDate(exception.date_from);
  return exception.date_to === exception.date_from ? from : `${from} to ${formatDate(exception.date_to)}`;
}

function exceptionLine(exception: ScheduleException): string {
  if (exception.closed || exception.opens === null || exception.closes === null) {
    return `Closed ${dates(exception)}`;
  }
  return `Open ${exception.opens} to ${exception.closes} on ${dates(exception)}`;
}

export function PublishedHours({ schedule, mode }: PublishedHoursProps): ReactElement {
  const modes = mode === null ? schedule.modes : schedule.modes.filter((entry) => entry.mode === mode);
  const exceptions = schedule.exceptions.filter((exception) => mode === null || exception.mode === null || exception.mode === mode);
  return (
    <details data-testid="storefront-published-hours" className="mt-2">
      <summary>Hours</summary>
      {modes.map((entry) => (
        <div key={entry.mode}>
          {mode === null && <p className="mt-1 font-semibold">{entry.mode}</p>}
          <ul>
            {shiftLines(entry).map((line, index) => (
              <li key={`${index}-${line}`}>{line}</li>
            ))}
          </ul>
        </div>
      ))}
      {exceptions.length > 0 && (
        <ul className="mt-1">
          {exceptions.map((exception, index) => (
            <li key={`${index}-${exception.date_from}`}>{exceptionLine(exception)}</li>
          ))}
        </ul>
      )}
    </details>
  );
}
